import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { copyFiles, getCrossS3 } from '../api/api';
import { FaCloud, FaFolder } from 'react-icons/fa';

const FileCopy = ({ setAlert }) => {
  const navigate = useNavigate();
  const [sourceType, setSourceType] = useState('s3');
  const [sourceConfig, setSourceConfig] = useState({
    bucket: '',
    region: 'us-east-1',
    prefix: '',
    ftpHost: '',
    ftpPort: '21',
    ftpUsername: '',
    ftpPassword: '',
    ftpPath: '/'
  });
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [bucketContents, setBucketContents] = useState([]);
  const [loadingContents, setLoadingContents] = useState(false);
  const [contentsLoaded, setContentsLoaded] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);

  const handleInputChange = (field, value) => {
    setSourceConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const testConnection = async () => {
    setIsConnecting(true);
    setConnectionStatus(null);

    try {
      if (sourceType === 's3') {
        // Validate required fields first
        if (!sourceConfig.bucket) {
          throw new Error('S3 bucket is required');
        }

        // Attempt to list the bucket using the API to verify access
        await getCrossS3(sourceConfig.bucket, sourceConfig.region);
      } else {
        if (!sourceConfig.ftpHost || !sourceConfig.ftpUsername) {
          throw new Error('FTP host and username are required');
        }

        // Placeholder for real FTP validation
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      setConnectionStatus('success');
      setAlert({
        variant: 'success',
        heading: 'Connection Successful',
        text: 'Successfully connected to the source location.'
      });

    } catch (error) {
      setConnectionStatus('error');
      setAlert({
        variant: 'error',
        heading: 'Connection Failed',
        text: error.message || 'Failed to connect to the source location.'
      });
    } finally {
      setIsConnecting(false);
    }
  };

  const loadBucketContents = async () => {
    setLoadingContents(true);
    try {
      const files = await getCrossS3(sourceConfig.bucket, sourceConfig.region);
      setBucketContents(files || []);
      setContentsLoaded(true);
    } catch (err) {
      setAlert({
        variant: 'error',
        heading: 'Unable to Load Bucket',
        text: err.message || 'Failed to retrieve bucket contents.'
      });
    } finally {
      setLoadingContents(false);
    }
  };

  const toggleFile = (item) => {
    setSelectedFiles(prev =>
      prev.some(f => f.Key === item.Key)
        ? prev.filter(f => f.Key !== item.Key)
        : [...prev, item]
    );
  };

  const startCopy = async () => {
    if (connectionStatus !== 'success') {
      setAlert({
        variant: 'warning',
        heading: 'Test Connection First',
        text: 'Please test the connection before starting the copy operation.'
      });
      return;
    }

    if (selectedFiles.length === 0) {
      setAlert({
        variant: 'warning',
        heading: 'No Files Selected',
        text: 'Please select at least one file to copy.'
      });
      return;
    }

    try {
      await copyFiles({
        ...sourceConfig,
        files: selectedFiles.map(f => f.Key)
      });
      
      setAlert({
        variant: 'success',
        heading: 'Copy Operation Started',
        text: 'Files are being copied in the background. You will be notified when complete.'
      });

      // Redirect to home page
      navigate('/');

    } catch (error) {
      console.error('Copy operation failed:', error);
      setAlert({
        variant: 'error',
        heading: 'Copy Failed',
        text: 'Failed to start copy operation. Please try again.'
      });
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Cloud Connector</h1>
        <p className="text-gray-600 mt-1">
          Connect to external storage systems and copy audio files for processing
        </p>
      </div>

      {/* Source Type Selection */}
      <div className="card">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Source Type</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className={`relative flex items-center p-4 border rounded-lg cursor-pointer transition-colors ${
            sourceType === 's3' ? 'border-cyan-500 bg-cyan-50' : 'border-gray-200 hover:bg-gray-50'
          }`}>
            <input
              type="radio"
              name="sourceType"
              value="s3"
              checked={sourceType === 's3'}
              onChange={(e) => setSourceType(e.target.value)}
              className="sr-only"
            />
            <div className="flex items-center space-x-3">
              <FaCloud className="text-2xl" />
              <div>
                <div className="font-medium text-gray-900">Amazon S3</div>
                <div className="text-sm text-gray-500">Connect to S3 bucket</div>
              </div>
            </div>
          </label>

          <label className={`relative flex items-center p-4 border rounded-lg cursor-pointer transition-colors ${
            sourceType === 'ftp' ? 'border-cyan-500 bg-cyan-50' : 'border-gray-200 hover:bg-gray-50'
          }`}>
            <input
              type="radio"
              name="sourceType"
              value="ftp"
              checked={sourceType === 'ftp'}
              onChange={(e) => setSourceType(e.target.value)}
              className="sr-only"
            />
            <div className="flex items-center space-x-3">
              <FaFolder className="text-2xl" />
              <div>
                <div className="font-medium text-gray-900">FTP/SFTP</div>
                <div className="text-sm text-gray-500">Connect to FTP server</div>
              </div>
            </div>
          </label>
        </div>
      </div>

      {/* Configuration Form */}
      <div className="card">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          {sourceType === 's3' ? 'S3 Configuration' : 'FTP Configuration'}
        </h3>

        {sourceType === 's3' ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bucket Name *
                </label>
                <input
                  type="text"
                  value={sourceConfig.bucket}
                  onChange={(e) => handleInputChange('bucket', e.target.value)}
                  className="input-field"
                  placeholder="my-audio-bucket"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Region
                </label>
                <select
                  value={sourceConfig.region}
                  onChange={(e) => handleInputChange('region', e.target.value)}
                  className="select-field"
                >
                  <option value="us-east-1">US East (N. Virginia)</option>
                  <option value="us-west-2">US West (Oregon)</option>
                  <option value="eu-west-1">Europe (Ireland)</option>
                  <option value="eu-central-1">Europe (Frankfurt)</option>
                  <option value="ap-southeast-1">Asia Pacific (Singapore)</option>
                </select>
              </div>
            </div>



            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Prefix (Optional)
              </label>
              <input
                type="text"
                value={sourceConfig.prefix}
                onChange={(e) => handleInputChange('prefix', e.target.value)}
                className="input-field"
                placeholder="recordings/2024/"
              />
              <p className="text-sm text-gray-500 mt-1">
                Only copy files with this prefix
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Host *
                </label>
                <input
                  type="text"
                  value={sourceConfig.ftpHost}
                  onChange={(e) => handleInputChange('ftpHost', e.target.value)}
                  className="input-field"
                  placeholder="ftp.example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Port
                </label>
                <input
                  type="number"
                  value={sourceConfig.ftpPort}
                  onChange={(e) => handleInputChange('ftpPort', e.target.value)}
                  className="input-field"
                  placeholder="21"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Username *
                </label>
                <input
                  type="text"
                  value={sourceConfig.ftpUsername}
                  onChange={(e) => handleInputChange('ftpUsername', e.target.value)}
                  className="input-field"
                  placeholder="username"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Password *
                </label>
                <input
                  type="password"
                  value={sourceConfig.ftpPassword}
                  onChange={(e) => handleInputChange('ftpPassword', e.target.value)}
                  className="input-field"
                  placeholder="Enter password"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Remote Path
              </label>
              <input
                type="text"
                value={sourceConfig.ftpPath}
                onChange={(e) => handleInputChange('ftpPath', e.target.value)}
                className="input-field"
                placeholder="/recordings/"
              />
              <p className="text-sm text-gray-500 mt-1">
                Path to the directory containing audio files
              </p>
            </div>
          </div>
        )}

        {/* Connection Status */}
        {connectionStatus && (
          <div className={`mt-4 p-3 rounded-lg ${
            connectionStatus === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
          }`}>
            <div className="flex items-center">
              {connectionStatus === 'success' ? (
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
              <span className="text-sm font-medium">
                {connectionStatus === 'success' ? 'Connection successful' : 'Connection failed'}
              </span>
            </div>
          </div>
        )}

        {connectionStatus === 'success' && !contentsLoaded && (
          <div className="mt-4">
            <button onClick={loadBucketContents} className="btn-outline" disabled={loadingContents}>
              {loadingContents ? 'Loading...' : 'Load Bucket Contents'}
            </button>
          </div>
        )}

        {contentsLoaded && (
          <div className="mt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Files in {sourceConfig.bucket}</h3>
            <div className="overflow-x-auto border rounded-lg">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Select</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">File Name</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Size</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Last Modified</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Storage Class</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {bucketContents.map(item => (
                    <tr key={item.Key}>
                      <td className="px-3 py-2 text-center">
                        <input
                          type="checkbox"
                          checked={selectedFiles.some(f => f.Key === item.Key)}
                          onChange={() => toggleFile(item)}
                        />
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">{item.Key}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{item.Size}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{item.LastModified}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{item.StorageClass}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between pt-6 mt-6 border-t border-gray-200">
          <button
            onClick={() => navigate('/uploadRecs')}
            className="btn-outline"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            Manual Upload Instead
          </button>

          <div className="flex space-x-3">
            <button
              onClick={testConnection}
              disabled={isConnecting}
              className="btn-outline disabled:opacity-50"
            >
              {isConnecting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Testing...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                  Test Connection
                </>
              )}
            </button>

            <button
              onClick={startCopy}
              disabled={connectionStatus !== 'success' || selectedFiles.length === 0}
              className="btn-primary disabled:opacity-50"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              Start Copy
            </button>
            <span className="text-sm text-gray-500 self-center">{selectedFiles.length} selected</span>
          </div>
        </div>
      </div>

      {/* Help Section */}
      <div className="bg-blue-50 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">
              Cloud Connector Tips
            </h3>
            <div className="mt-2 text-sm text-blue-700">
              <ul className="list-disc list-inside space-y-1">
                <li>Ensure your credentials have read access to the source location</li>
                <li>Only audio files will be copied and processed</li>
                <li>Large files may take some time to process</li>
                <li>You'll receive notifications when the copy operation completes</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FileCopy;